import { useState, useEffect } from 'react'
import { Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import './PasswordProtection.css'

const ADMIN_PASSWORD = 'Xpadmin04'
const AUTH_STORAGE_KEY = 'easter_admin_auth'

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const auth = sessionStorage.getItem(AUTH_STORAGE_KEY)
    if (auth === 'true') {
      setIsAuthenticated(true)
    }
    setIsLoading(false)
  }, [])

  const login = (password) => {
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem(AUTH_STORAGE_KEY, 'true')
      setIsAuthenticated(true)
      return true
    }
    return false
  }

  const logout = () => {
    sessionStorage.removeItem(AUTH_STORAGE_KEY)
    setIsAuthenticated(false)
  }

  return { isAuthenticated, isLoading, login, logout }
}

export function PasswordProtection({ children, onNavigateBack }) {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { isAuthenticated, isLoading, login } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    // Small delay to prevent brute force and show loading state
    await new Promise(resolve => setTimeout(resolve, 300))

    const success = login(password)
    if (!success) {
      setError('Incorrect password. Please try again.')
      setPassword('')
    }

    setIsSubmitting(false)
  }

  if (isLoading) {
    return (
      <div className="password-protection">
        <div className="password-card loading">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  if (isAuthenticated) {
    return children
  }

  return (
    <div className="password-protection">
      <div className="password-backdrop">
        <div className="backdrop-orb orb-1" />
        <div className="backdrop-orb orb-2" />
        <div className="backdrop-orb orb-3" />
      </div>

      <button className="nav-back" onClick={onNavigateBack}>
        <ArrowLeft size={20} />
      </button>

      <div className="password-card">
        <div className="password-icon">
          <Lock size={32} />
        </div>

        <h1>Admin Access</h1>
        <p className="password-subtitle">
          This area is restricted. Please enter the admin password to continue.
        </p>

        <form onSubmit={handleSubmit} className="password-form">
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className={`password-input ${error ? 'error' : ''}`}
              autoFocus
              disabled={isSubmitting}
            />
            <button
              type="button"
              className="toggle-visibility"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={!password || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="btn-spinner" />
                Verifying...
              </>
            ) : (
              'Enter'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
